import React from "react";
import { ChevronDown, ShieldCheck, CreditCard, LifeBuoy, Layers } from "lucide-react";
import { formatCompact, formatMonths, formatPercent } from "@/lib/currency";
import { resilienceHeadline } from "@/lib/calculations";

// Correction #1 — makes the FROZEN V2.1 resilience score explainable WITHOUT
// touching calculations.js. Reads only derived metrics already produced by the
// engine (scoreBreakdown, ratios, runway). Higher = better everywhere.

const TONE = {
  good: "border-emerald-200 bg-emerald-50 text-emerald-700",
  ok: "border-emerald-100 bg-emerald-50/60 text-emerald-700",
  warn: "border-amber-200 bg-amber-50 text-amber-700",
  bad: "border-rose-200 bg-rose-50 text-rose-700",
};
const DOT = { good: "bg-emerald-500", ok: "bg-emerald-400", warn: "bg-amber-500", bad: "bg-rose-500" };

function Row({ Icon, title, verdict, tone, children, testId }) {
  return (
    <div className={`rounded-2xl border p-4 ${TONE[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" />
        <p className="font-heading text-sm font-bold text-zinc-900">{title}</p>
        <span className="ml-auto flex items-center gap-1 text-[11px] font-bold uppercase">
          <span className={`h-1.5 w-1.5 rounded-full ${DOT[tone]}`} />
          {verdict}
        </span>
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-zinc-600">{children}</p>
    </div>
  );
}

export function ResilienceExplainer({ metrics, hero }) {
  if (!metrics) return null;
  const b = metrics.scoreBreakdown || {};
  const em = metrics.emergencyMonths || 0;
  const debtPct = metrics.debtToCoreIncome || 0;
  const dep = metrics.sideDependency || 0;

  // C1 Emergency fund
  const c1 = em >= 6 ? "good" : em >= 3 ? "ok" : em >= 1 ? "warn" : "bad";
  const c1v = em >= 6 ? "SANGAT BAIK" : em >= 3 ? "BAIK" : em >= 1 ? "PERLU DIPERKUAT" : "KRITIS";

  // C2 Debt burden
  const c2 = debtPct <= 20 ? "good" : debtPct <= 40 ? "ok" : debtPct <= 60 ? "warn" : "bad";
  const c2v = debtPct <= 20 ? "RINGAN" : debtPct <= 40 ? "SEDANG" : debtPct <= 60 ? "BERAT" : "SANGAT BERAT";

  // C3 Post-shock survival
  const runway = hero?.after?.survivalRunway;
  const positive = hero?.after?.isPositive;
  const c3score = b.shock ?? 0;
  const c3 = positive || c3score >= 27 ? "good" : c3score >= 18 ? "ok" : c3score >= 9 ? "warn" : "bad";
  const c3v = positive ? "SANGAT BAIK" : c3score >= 27 ? "BAIK" : c3score >= 18 ? "CUKUP" : c3score >= 9 ? "LEMAH" : "KRITIS";

  // C4 Side-income dependency
  const c4 = dep <= 15 ? "good" : dep <= 40 ? "ok" : dep <= 60 ? "warn" : "bad";
  const c4v = dep <= 15 ? "RENDAH" : dep <= 40 ? "SEDANG" : dep <= 60 ? "TINGGI" : "SANGAT TINGGI";

  const rows = [
    {
      key: "c1", Icon: ShieldCheck, title: "Dana Darurat", verdict: c1v, tone: c1,
      text: em >= 6
        ? `Dana darurat dapat menutup sekitar ${formatMonths(em)} bulan pengeluaran wajib — target ketahanan tercapai.`
        : `Dana darurat menutup sekitar ${formatMonths(em)} bulan pengeluaran wajib. Idealnya minimal 6 bulan.`,
    },
    {
      key: "c2", Icon: CreditCard, title: "Beban Cicilan", verdict: c2v, tone: c2,
      text: `Sekitar ${formatPercent(debtPct)}% penghasilan utama dipakai untuk cicilan.${debtPct > 40 ? " Ini melemahkan ketahanan keuangan secara signifikan." : ""}`,
    },
    {
      key: "c3", Icon: LifeBuoy, title: "Ketahanan Jika Penghasilan Terganggu", verdict: c3v, tone: c3,
      text: positive
        ? "Jika penghasilan utama terbesar berhenti, cashflow keluarga diperkirakan tetap positif."
        : `Jika penghasilan utama terbesar berhenti, keluarga diperkirakan dapat bertahan sekitar ${runway != null ? formatMonths(runway) : "beberapa"} bulan pada skenario saat ini.`,
    },
    {
      key: "c4", Icon: Layers, title: "Ketergantungan Penghasilan Tambahan", verdict: c4v, tone: c4,
      text: dep <= 0
        ? "Keluarga tidak bergantung pada penghasilan tambahan."
        : `Sekitar ${formatPercent(dep)}% penghasilan keluarga berasal dari penghasilan tambahan.${dep > 40 ? " Kondisi cukup sensitif bila penghasilan ini hilang." : ""}`,
    },
  ];

  const rawRows = [
    { label: "Dana Darurat", got: b.emergency ?? 0, max: 30 },
    { label: "Beban Cicilan", got: b.debt ?? 0, max: 25 },
    { label: "Bertahan Pasca-Guncangan", got: b.shock ?? 0, max: 30 },
    { label: "Kerentanan Penghasilan Tambahan", got: b.sideVulnerability ?? 0, max: 15 },
  ];

  return (
    <div className="mt-5 rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow" data-testid="resilience-explainer">
      <p className="mb-1 font-heading text-base font-bold text-zinc-900">Apa arti skor Anda?</p>
      <p className="mb-4 text-xs leading-relaxed text-zinc-500" data-testid="resilience-why">
        {resilienceHeadline(metrics)}
      </p>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <Row key={r.key} Icon={r.Icon} title={r.title} verdict={r.verdict} tone={r.tone} testId={`explain-${r.key}`}>
            {r.text}
          </Row>
        ))}
      </div>

      <details className="mt-4 rounded-2xl bg-zinc-50 p-3" data-testid="score-breakdown-details">
        <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-zinc-700">
          <span>Bagaimana skor dihitung?</span>
          <ChevronDown className="h-4 w-4 text-zinc-400" />
        </summary>
        <div className="mt-3 space-y-2">
          {rawRows.map((r) => (
            <div key={r.label} data-testid={`breakdown-${r.label}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-zinc-600">{r.label}</span>
                <span className="font-mono font-bold text-zinc-900">
                  {String(r.got).replace(".", ",")}<span className="text-zinc-400">/{r.max}</span>
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
                <div className="h-full rounded-full bg-zinc-800" style={{ width: `${Math.min(100, (r.got / r.max) * 100)}%` }} />
              </div>
            </div>
          ))}
          <div className="flex items-baseline justify-between border-t border-zinc-200 pt-2 text-xs">
            <span className="font-semibold text-zinc-700">Skor akhir</span>
            <span className="font-mono font-black text-zinc-900">{metrics.score}<span className="text-zinc-400">/100</span></span>
          </div>
          <p className="pt-1 text-[11px] leading-relaxed text-zinc-400">
            Tiap komponen dihitung dari kondisi finansial Anda (dana darurat, rasio cicilan, sisa pendapatan saat guncangan,
            ketergantungan penghasilan tambahan), lalu dijumlahkan menjadi skor 0–100. Semakin tinggi angka tiap komponen,
            semakin sehat. HIJAU ≥70 · KUNING 45–69 · MERAH &lt;45.
          </p>
        </div>
      </details>
    </div>
  );
}
